import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeftIcon, EnvelopeSimpleIcon } from "@phosphor-icons/react";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
      toast.success(data.message);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="forgot-password-page">
      <Navbar />
      <main className="flex-1 max-w-md mx-auto w-full px-6 py-16">
        <button
          onClick={() => navigate("/login")}
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 mb-6"
          data-testid="forgot-back"
        >
          <ArrowLeftIcon size={14} /> Back to sign in
        </button>

        {sent ? (
          <div className="text-center" data-testid="forgot-sent-state">
            <div className="h-14 w-14 mx-auto bg-emerald-100 text-emerald-700 rounded-full grid place-items-center mb-4">
              <EnvelopeSimpleIcon size={28} weight="fill" />
            </div>
            <h1 className="font-display font-black text-3xl tracking-tight">Check your inbox</h1>
            <p className="text-zinc-600 mt-3 text-sm leading-relaxed">
              If <span className="font-semibold">{email}</span> belongs to a Bidorah account,
              a reset link is on its way. The link expires in <b>60 minutes</b>.
            </p>
            <p className="text-zinc-500 mt-4 text-xs">
              Didn&apos;t get it? Check spam, or{" "}
              <button
                className="text-[#1D78AB] underline font-semibold"
                onClick={() => { setSent(false); setEmail(""); }}
                data-testid="forgot-try-again"
              >
                try a different email
              </button>.
            </p>
          </div>
        ) : (
          <>
            <h1 className="font-display font-black text-4xl tracking-tight">Forgot your password?</h1>
            <p className="text-zinc-600 mt-2 text-sm">
              Enter the email you signed up with and we&apos;ll send you a reset link.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <Input
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11"
                data-testid="forgot-email-input"
              />
              <Button
                type="submit"
                disabled={sending || !email}
                className="w-full h-11 bg-[#1D78AB] hover:bg-[#165d85] text-white"
                data-testid="forgot-submit-btn"
              >
                {sending ? "Sending…" : "Send reset link"}
              </Button>
            </form>
            <p className="text-xs text-zinc-500 mt-6">
              Remembered it?{" "}
              <Link to="/login" className="text-[#1D78AB] font-semibold hover:underline">
                Back to sign in
              </Link>
            </p>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
